/**
 * Pilot format helpers — dollar/day/FTE formatting and the count-up
 * animation primitive. Extracted out of the soon-to-die `render.js`
 * during Phase 5 of the React-engine port (see
 * `docs/eng/pilot-react-engine.md`). Pure functions and one DOM-touching
 * animation utility that consumers may still want from imperative code
 * paths (sandbox modal "captured" pills, story scene number reveals).
 *
 * No DOM queries here — `animateNumberValue` is handed a node by its
 * caller. React components inside `components/pilot/` should prefer a
 * `useEffect` + ref over calling this helper directly.
 */
/* eslint-disable @typescript-eslint/no-explicit-any -- animateNumberValue
   stashes its own rAF / timeout handles on the target node to support
   re-entrant calls. The data-attribute round-trip is the existing
   contract; typing it stricter would mean reshaping every caller. */

/**
 * Render a wall-clock duration in days. Sub-day values fall back to
 * hours. Used by sandbox tax / wins labels and story scene reveals.
 */
export function fmtDays(d: number): string {
  if (!isFinite(d)) return "—";
  if (d < 1) return `${(d * 24).toFixed(1)}h`;
  return `${d.toFixed(1)}d`;
}

/**
 * Render a dollar figure with K/M abbreviation. Sandbox + story share
 * one formatter so the comma-vs-abbrev choice is consistent.
 */
export function fmtUSD(n: number): string {
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}k`;
  return `$${Math.round(n)}`;
}

/**
 * Express a wall-clock hour total in FTE-equivalent terms ("≈ 0.6 FTEs
 * of work" / "≈ 12.3 work-weeks"). Anchors abstract hour counts in a
 * tangible human scale.
 */
export function fteEquivalent(hoursPerYear: number): string {
  const HRS_PER_WEEK = 40;
  const WEEKS_PER_YEAR = 50; // ~2 weeks PTO
  const hoursPerFte = HRS_PER_WEEK * WEEKS_PER_YEAR; // 2000
  const fte = hoursPerYear / hoursPerFte;
  if (fte >= 0.5) return `≈ ${fte.toFixed(1)} FTEs of work`;
  const weeks = hoursPerYear / HRS_PER_WEEK;
  return `≈ ${weeks.toFixed(1)} work-weeks`;
}

/**
 * Tick-up animation for a numeric DOM node. The legacy story.js engine
 * calls this from outside React with `document.querySelector` handles;
 * React components inside `components/pilot/` should prefer a custom
 * `useEffect` over importing it. Stashes its own rAF / timeout handles
 * on the target node to support re-entrant calls (a second `animate`
 * before the first finishes is safe — the first is cancelled).
 */
export function animateNumberValue(
  el: HTMLElement | null,
  target: number,
  format: (n: number) => string,
  duration = 900,
): void {
  if (!el) return;
  const anyEl = el as any;
  if (anyEl._animRaf) cancelAnimationFrame(anyEl._animRaf);
  if (anyEl._animFallback) clearTimeout(anyEl._animFallback);
  const startVal = parseFloat(el.dataset.lastValue || "0") || 0;
  const t0 = performance.now();
  const ease = (x: number) => 1 - Math.pow(1 - x, 3);
  let done = false;
  const finalize = () => {
    if (done) return;
    done = true;
    el.textContent = format(target);
    el.dataset.lastValue = String(target);
    if (anyEl._animRaf) cancelAnimationFrame(anyEl._animRaf);
    anyEl._animRaf = null;
  };
  function step(now: number) {
    if (done) return;
    const k = Math.min(1, (now - t0) / duration);
    const v = startVal + (target - startVal) * ease(k);
    if (el) el.textContent = format(v);
    if (k < 1) anyEl._animRaf = requestAnimationFrame(step);
    else finalize();
  }
  anyEl._animRaf = requestAnimationFrame(step);
  anyEl._animFallback = setTimeout(finalize, duration + 80);
}
